'use strict'

import CXImageAction from './../ImagePaintProcessing/CXImageAction.js'
import Config from './../Config.js'
import Utils from './../Utils.js'
import { DOM } from './../ui/DOM.js'
import { Selector } from './../ui/Selectors.js'
import { TOOL } from './../constants/Tools.js'

/**
 * DrawingToolActionHandler
 * Encapsulates painting tool behaviors and pointer state (brush/eraser/polygon/magic).
 * Keeps pointer-handling logic separate from DOM wiring for easier reuse in tests and other UIs.
 */
class DrawingToolActionHandler {
    /** @param {Object} application - Visualizer Application instance. */
    constructor(application) {
        this.app = application
        this.isDrawing = false
        this.mouse = { x: 0, y: 0, down: false }
    }

    /**
     * Pointer down dispatcher based on active tool.
     * @param {number} x - Canvas x coordinate.
     * @param {number} y - Canvas y coordinate.
     * @returns {void}
     */
    onMouseDown(x, y) {
        const app = this.app
        switch (app.toolManager.activeTool) {
            case TOOL.DRAW_LINE:
            case TOOL.DRAW_POLY_MINUS:
            case TOOL.DRAW_POLY_PLUS:
                return this._handlePolygonToolDown(x, y)
            case TOOL.BRUSH:
                return this._handleBrushDown(x, y)
            case TOOL.ERASER:
                return this._handleEraserDown(x, y)
            case TOOL.MAGIC:
                return this._handleMagicHandDown(x, y)
            case TOOL.HAND:
                // Hand pan is handled by zoom controls; no drawing action here.
                return
            default:
                return
        }
    }

    /**
     * Pointer move handler: polygon preview and brush/eraser strokes.
     * @param {number} x - Canvas x coordinate.
     * @param {number} y - Canvas y coordinate.
     * @returns {void}
     */
    onMouseMove(x, y) {
        const app = this.app

        this.mouse.x = x
        this.mouse.y = y

        const isPolygonTool =
            app.toolManager.activeTool === TOOL.DRAW_LINE ||
            app.toolManager.activeTool === TOOL.DRAW_POLY_MINUS ||
            app.toolManager.activeTool === TOOL.DRAW_POLY_PLUS

        if (isPolygonTool && app.canvasState.isPainting) {
            const ctx = app.canvasState.viewimage_draw.context
            const overlay = app.canvasState.viewimage_draw

            ctx.clearRect(0, 0, overlay.width, overlay.height)

            const current = app.canvasState.getCurrentPolygon()
            if (!current) return

            const isNearStart = current.distance(x, y) && current.dots.length > 2
            const fillColor = isNearStart ? 'rgba(46, 204, 113, 0.35)' : 'rgba(231, 76, 60, 0.25)'
            current.draw({ fillColor })
            ctx.beginPath()
            ctx.moveTo(current.lastDotX(), current.lastDotY())

            if (isNearStart) {
                ctx.lineTo(current.dots[0].x, current.dots[0].y)
            } else {
                ctx.lineTo(x, y)
            }
            ctx.stroke()
            this._drawPolygonSnapRing(ctx, current.dots[0].x, current.dots[0].y, isNearStart)
            return
        }

        if (
            (app.toolManager.activeTool === TOOL.BRUSH && this.isDrawing) ||
            (app.toolManager.activeTool === TOOL.ERASER && this.isDrawing)
        ) {
            app.toolManager.drawOnTouch(x, y)
        }
    }

    /**
     * Pointer up handler: finalize brush/eraser states.
     * @returns {void}
     */
    onMouseUp() {
        const app = this.app

        if (app.toolManager.activeTool === TOOL.BRUSH && this.isDrawing) {
            if (app.canvasState.colorSample.workingColorPos === 0) {
                app.alertColorNotSelected()
            }
            this.isDrawing = false
            DOM.hide(Selector.btnRotate)
        } else if (app.toolManager.activeTool === TOOL.ERASER) {
            this.isDrawing = false
        }

        this.mouse.down = false
    }

    /**
     * Handle polygon/line tools on pointer down.
     * Starts or extends a polygon; closes when near the first point (or after 2 points for a line).
     * @param {number} x - Canvas x coordinate.
     * @param {number} y - Canvas y coordinate.
     * @returns {void}
     */
    _handlePolygonToolDown(x, y) {
        const app = this.app
        const needsColor = app.toolManager.activeTool !== TOOL.DRAW_POLY_MINUS
        if (needsColor && app.canvasState.colorSample.workingColorPos === 0) {
            app.alertColorNotSelected()
            return
        }

        this.mouse.x = x
        this.mouse.y = y

        if (!app.canvasState.isPainting) {
            app.canvasState.isPainting = true
            app.canvasState.startPolygon({
                x: this.mouse.x,
                y: this.mouse.y,
                color: app.canvasState.colorSample.getCurrentColor(),
                numcolor: parseInt(app.canvasState.colorSample.workingColorPos, 10),
            })
        } else {
            const fill = app.toolManager.activeTool === TOOL.DRAW_POLY_PLUS || app.toolManager.activeTool === TOOL.DRAW_LINE
            const current = app.canvasState.getCurrentPolygon()
            const isNearStart = current.distance(this.mouse.x, this.mouse.y)

            if (isNearStart) {
                const first = current.dots[0]
                current.add(first.x, first.y)
                app.canvasState.finishPolygon(fill)
            } else {
                current.add(x, y)
                if (app.toolManager.activeTool === TOOL.DRAW_LINE) {
                    app.canvasState.finishPolygon(fill)
                }
            }
        }

        this.mouse.down = true
    }

    /**
     * Brush tool: start painting stroke.
     * Adds an undoable paint action and draws the first segment.
     * @param {number} x - Canvas x coordinate.
     * @param {number} y - Canvas y coordinate.
     * @returns {void}
     */
    _handleBrushDown(x, y) {
        const app = this.app

        if (app.canvasState.colorSample.workingColorPos === 0) {
            app.alertColorNotSelected()
            return
        }
        if (this.isDrawing) return

        this.isDrawing = true
        app.canvasState.viewimage.context.lineWidth = app.toolManager.brushSize

        const action = new CXImageAction(CXImageAction.ActionType.ActionTypePaint)
        action.setColorSample(app.canvasState.colorSample)
        app.canvasState.addAction(action)

        app.toolManager.drawOnTouch(x, y)
    }

    /**
     * Eraser tool: start erasing stroke.
     * Adds an undoable erase action and draws the first segment.
     * @param {number} x - Canvas x coordinate.
     * @param {number} y - Canvas y coordinate.
     * @returns {void}
     */
    _handleEraserDown(x, y) {
        const app = this.app

        this.isDrawing = true
        app.canvasState.viewimage.context.lineWidth = app.toolManager.brushSize
        app.canvasState.addAction(new CXImageAction(CXImageAction.ActionType.ActionTypeErase))

        app.toolManager.drawOnTouch(x, y)
    }

    /**
     * Magic wand tool: flood fill on untouched pixels.
     * Debounced via toolManager flags; sends to image processing only if the pixel matches the original.
     * @param {number} x - Canvas x coordinate.
     * @param {number} y - Canvas y coordinate.
     * @returns {void}
     */
    _handleMagicHandDown(x, y) {
        const app = this.app

        if (app.canvasState.colorSample.workingColorPos === 0) {
            app.alertColorNotSelected()
            return
        }

        if (!app.toolManager.isMagicWandProcessing) {
            const action = new CXImageAction(CXImageAction.ActionType.ActionTypeMagicPaint)
            action.initWithActionTypeMagicPaint()
            action.setColorSample(app.canvasState.colorSample)
            app.canvasState.addAction(action)
            app.toolManager.currentMagicAction = action

            app.toolManager.isMagicWandProcessing = true

            const currentPoint = app.canvasState.getResultImageImageData(x, y, 1, 1).data
            const originalPoint = app.canvasState.getOriginalImageImageData(x, y, 1, 1).data
            if (Utils.arraysEqual(currentPoint, originalPoint)) {
                DOM.hide(Selector.btnRotate)
                app.toolManager.computeMagicWand({
                    paintLevel: Config.MAGIC_WAND_DEFAULT_LEVEL,
                    isFirstCall: true,
                    coordinates: { x, y },
                })
            }
        } else if (!app.toolManager.imgProObj?.isProcessing) {
            app.toolManager.isMagicWandProcessing = false
        }
    }

    /**
     * Draw a small zoom lens around the polygon start point to help targeting closure.
     * @param {Object} config
     * @param {CanvasRenderingContext2D} config.ctx
     * @param {HTMLCanvasElement} config.sourceCanvas
     * @param {number} config.x
     * @param {number} config.y
     */
    _drawPolygonSnapRing(ctx, x, y, isNearStart = false) {
        if (!ctx) return

        ctx.save()
        ctx.strokeStyle = isNearStart ? '#2ecc71' : 'rgba(46, 204, 113, 0.6)'
        ctx.lineWidth = isNearStart ? 3.5 : 2.5
        ctx.beginPath()
        ctx.arc(x, y, isNearStart ? 9 : 7, 0, Math.PI * 2)
        ctx.stroke()
        ctx.restore()
    }
}

export default DrawingToolActionHandler
